import { faker } from '@faker-js/faker'
import {
  addDays,
  eachDayOfInterval,
  getDay,
  getMonth,
  getYear,
  isBefore,
  isFriday,
  lastDayOfMonth,
  set,
  startOfMonth,
  subMonths,
} from 'date-fns'

const NOON_CONFIG = { hours: 12, minutes: 0, seconds: 0, milliseconds: 0 }

export const isObject = (value: unknown): value is object => {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export const randomIntegerInRange = (min: number, max: number): number => {
  return faker.number.int({ min, max })
}

export const past12Months = (): Array<[number, number]> => {
  const now = new Date()

  return Array.from({ length: 12 }, (_, i) => {
    const date = subMonths(now, 11 - i)
    return [getYear(date), getMonth(date) + 1]
  })
}

export const past12MonthsFridays = (): Date[] => {
  const now = set(new Date(), NOON_CONFIG)
  const start = set(subMonths(now, 12), NOON_CONFIG)

  return eachDayOfInterval({ start, end: now })
    .filter(date => isFriday(date) && date <= now)
    .map(date => set(date, NOON_CONFIG))
    .reverse()
    .slice(1)
}

export const oneDayMonthlyPast12Months = (dayOfWeek: number): Date[] => {
  const now = set(new Date(), NOON_CONFIG)

  return Array.from({ length: 12 }, (_, i) => {
    const monthStart = startOfMonth(subMonths(now, 11 - i))

    // Find the first occurrence of the target day in the month
    const offset = (dayOfWeek - getDay(monthStart) + 7) % 7 // ensure positive mod result
    const firstOccurrence = addDays(monthStart, offset)

    // Then get the 3rd occurrence by adding 14 days
    return set(addDays(firstOccurrence, 14), NOON_CONFIG)
  }).filter(date => isBefore(date, now) || date.getTime() === now.getTime())
}

export const randomDateInYearMonth = (year: number, month: number): Date => {
  const baseDate = new Date(year, month - 1)
  const lastDay = lastDayOfMonth(baseDate).getDate()
  const randomDay = Math.floor(Math.random() * lastDay) + 1

  return set(baseDate, {
    date: randomDay,
    hours: 15,
    minutes: 46,
    seconds: 54,
    milliseconds: 255,
  })
}

export const constructFullAddress = () => {
  const fullAddress =
    `${faker.location.streetAddress()}\n`
    + `${faker.location.secondaryAddress()}\n`
    + `${faker.location.city()}, ${faker.location.state({ abbreviated: true })} ${faker.location.zipCode('#####')}`

  return fullAddress
}

export function allocateRandomChunks(total: number, chunks: number): number[] {
  // Create `chunks - 1` random cut points between 0 and total
  const cuts = Array.from(
    { length: chunks - 1 },
    () => faker.number.float() * total,
  ).sort((a, b) => a - b)

  // Add 0 and total to create the range
  const points = [0, ...cuts, total]

  // Get chunk sizes by computing differences between consecutive points
  return points.slice(1).map((p, i) => +Math.round(p - points[i]))
}

export const getRandomBoolean = (probability: number) =>
  faker.datatype.boolean(probability)

export type MonthlyDistribution = {
  mean: number    // $/month
  stdDev: number  // $/month
  min: number     // hard floor $/month
  max: number     // hard ceiling $/month
}

export function sampleNormal(
  mean: number,
  stdDev: number,
  min: number,
  max: number,
): number {
  const u1 = Math.random() || Number.EPSILON
  const u2 = Math.random()
  const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2)
  return Math.max(min, Math.min(max, Math.round(mean + z * stdDev)))
}
